// A price in the currency picked in the search form's currency dropdown, shown
// as an extra line under the price the API sent (that price is never changed).
// The pick and the currency_ratio response are kept in sessionStorage, so the
// traveller details, seat map, payment and confirmation pages show the same conversion.

import axios from "axios";

const RATIO_KEY = "currencyRatio";
const PICK_KEY = "conversionCurrency";

// Results show these currencies as symbols; any other value is the ISO code
const SYMBOL_CODES = { "US$": "USD", "CA$": "CAD", "C$": "CAD", "₹": "INR" };

export const toCurrencyCode = (value) => {
  const text = String(value || "").trim();
  return SYMBOL_CODES[text] || text.toUpperCase();
};

export const saveConversionCurrency = (code) => {
  try {
    if (code) sessionStorage.setItem(PICK_KEY, code);
  } catch (e) {
    // Storage unavailable: pages just show no conversion
  }
};

// Back on the home page the pick starts over from the base currency
export const clearConversionCurrency = () => {
  try {
    sessionStorage.removeItem(PICK_KEY);
  } catch (e) {
    // Storage unavailable: nothing was kept
  }
};

export const getConversionCurrency = () => {
  try {
    return sessionStorage.getItem(PICK_KEY) || "";
  } catch (e) {
    return "";
  }
};

// The stored currency_ratio response, or null when there is none
const getStoredCurrencyRatio = () => {
  try {
    return JSON.parse(sessionStorage.getItem(RATIO_KEY));
  } catch (e) {
    return null;
  }
};

const saveCurrencyRatio = (data) => {
  try {
    sessionStorage.setItem(RATIO_KEY, JSON.stringify(data));
  } catch (e) {
    // Storage unavailable: later pages just show no conversion
  }
};

// Request already on its way, so the home page and the search page share one call
let pendingRatio = null;

/**
 * The currency_ratio response for `baseCurrency`, e.g.
 * {"status":true,"base_currency":"CAD","rates":{"USD":0.715,...}}, or null when
 * there is no endpoint or the call fails. Reuses the copy stored this session
 * for the same base unless `refresh` is set (each page load fetches fresh rates).
 */
export const fetchCurrencyRatio = (api, baseCurrency, { refresh = false } = {}) => {
  if (!api || !baseCurrency) return Promise.resolve(null);

  if (pendingRatio?.baseCurrency === baseCurrency) return pendingRatio.request;
  const stored = getStoredCurrencyRatio();
  if (!refresh && stored?.base_currency === baseCurrency) return Promise.resolve(stored);

  // The config URL may come with a {currency} placeholder or without one;
  // either way the currency goes on the path
  const url = api.includes("{currency}")
    ? api.replace("{currency}", baseCurrency)
    : `${api.replace(/\/+$/, "")}/${baseCurrency}`;
  const request = axios
    .get(url)
    .then((response) => {
      const data = response.data;
      if (!data?.status || !data.base_currency) return null;
      saveCurrencyRatio(data);
      return data;
    })
    .catch((error) => {
      console.log(error, "currencyRatioError");
      return null;
    })
    .finally(() => {
      if (pendingRatio?.request === request) pendingRatio = null;
    });
  pendingRatio = { baseCurrency, request };
  return request;
};

// Units of each currency per 1 base currency, e.g. { CAD: 1, USD: 0.72 }
export const getCurrencyRates = () => {
  const data = getStoredCurrencyRatio();
  if (!data?.base_currency) return {};
  return { ...data.rates, [data.base_currency]: 1 };
};

/**
 * `amount` (a number or formatted text like "504,408.49") in `fromCurrency`
 * (code or result symbol) as e.g. "USD 2,145.56", or "" when there is nothing
 * to show: no pick, the same currency, or no rate for either currency.
 * `toCurrency` and `rates` default to what sessionStorage holds.
 */
export const convertForDisplay = (amount, fromCurrency, { toCurrency, rates } = {}) => {
  const from = toCurrencyCode(fromCurrency);
  const to = toCurrency || getConversionCurrency();
  const allRates = rates || getCurrencyRates();
  const fromRate = allRates[from];
  const toRate = allRates[to];
  if (!to || from === to || !fromRate || !toRate) return "";

  const value = parseFloat(String(amount).replace(/[^0-9.-]+/g, ""));
  if (isNaN(value)) return "";

  const converted = (value / fromRate) * toRate;
  return `${to} ${converted.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
